import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../components/auth/AuthProvider';
import { supabase } from '../lib/supabase';
import { Loader2 } from 'lucide-react';
import { isProfileCompleteAny } from '../hooks/useProfile';

// DEV-only redirect logging helper
const logRedirect = (from: string, to: string, reason: string) => {
    if (import.meta.env.DEV) {
        console.log(`[Redirect] ${from} → ${to} (${reason})`);
    }
};

/**
 * Parse full_name from Google OAuth user_metadata into first_name and last_name
 * Returns { firstName: string | null, lastName: string | null }
 */
const parseGoogleName = (fullName: string | undefined | null): { firstName: string | null; lastName: string | null } => {
    if (!fullName || typeof fullName !== 'string') {
        return { firstName: null, lastName: null };
    }
    
    const trimmed = fullName.trim();
    if (!trimmed) {
        return { firstName: null, lastName: null };
    }
    
    const parts = trimmed.split(/\s+/);
    if (parts.length === 1) {
        // Only one name provided - use as first name
        return { firstName: parts[0], lastName: null };
    }
    
    // First part is first name, rest is last name
    return {
        firstName: parts[0],
        lastName: parts.slice(1).join(' ')
    };
};

/**
 * Ensure profile exists for OAuth user and upsert first_name/last_name if available
 */
const ensureProfileForOAuthUser = async (userId: string, userEmail: string | undefined, userMetadata: any): Promise<void> => {
    try {
        // Check if profile exists
        const { data: existingProfile, error: fetchError } = await supabase
            .from('profiles')
            .select('id, first_name, last_name')
            .eq('id', userId)
            .maybeSingle();
        
        // If profile exists and has names, we're done
        if (existingProfile && existingProfile.first_name && existingProfile.last_name) {
            if (import.meta.env.DEV) {
                console.log('[AuthCallback] Profile already exists with names');
            }
            return;
        }
        
        // Parse name from Google metadata - check multiple sources
        // Priority: given_name/family_name > full_name > name
        let firstName: string | null = null;
        let lastName: string | null = null;
        
        // First try: given_name and family_name (most reliable for Google)
        if (userMetadata?.given_name || userMetadata?.family_name) {
            firstName = userMetadata.given_name?.trim() || null;
            lastName = userMetadata.family_name?.trim() || null;
        } else {
            // Fallback: Parse full_name or name
            const googleFullName = userMetadata?.full_name || userMetadata?.name;
            const parsed = parseGoogleName(googleFullName);
            firstName = parsed.firstName;
            lastName = parsed.lastName;
        }
        
        // Prepare upsert data
        const profileData: any = {
            id: userId,
            email: userEmail || null
        };
        
        // Only set first_name/last_name if we have them and profile doesn't already have them
        if (firstName && !existingProfile?.first_name) {
            profileData.first_name = firstName;
        }
        if (lastName && !existingProfile?.last_name) {
            profileData.last_name = lastName;
        }
        
        // Upsert profile (insert or update)
        const { error: upsertError } = await supabase
            .from('profiles')
            .upsert(profileData, {
                onConflict: 'id',
                ignoreDuplicates: false
            });
        
        if (upsertError) {
            console.error('[AuthCallback] Error upserting profile:', upsertError);
            // Don't throw - profile might already exist via trigger
        } else {
            if (import.meta.env.DEV) {
                console.log('[AuthCallback] Profile upserted successfully', { firstName, lastName });
            }
        }
    } catch (err) {
        console.error('[AuthCallback] Error ensuring profile:', err);
        // Don't throw - continue with routing even if profile upsert fails
    }
};

const AuthCallback = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { authReady, session, userId } = useAuth();
    const redirectDoneRef = useRef(false);
    const profileCheckedRef = useRef(false);

    useEffect(() => {
        // NEVER redirect unless authReady === true
        if (!authReady) {
            if (import.meta.env.DEV) console.log('[AuthCallback] Waiting for authReady');
            return;
        }

        // Guard: Only redirect once per page load
        if (redirectDoneRef.current) {
            if (import.meta.env.DEV) console.log('[AuthCallback] Redirect already done, skipping');
            return;
        }

        // Handle OAuth code exchange (if using PKCE flow)
        const handleOAuthCallback = async () => {
            // Check if URL has code parameter (OAuth callback)
            const urlParams = new URLSearchParams(location.search);
            const code = urlParams.get('code');
            
            if (code && !session) {
                if (import.meta.env.DEV) {
                    console.log('[AuthCallback] Exchanging OAuth code for session');
                }
                
                try {
                    // Exchange code for session (Supabase PKCE flow)
                    const { data, error } = await supabase.auth.exchangeCodeForSession(code);
                    
                    if (error) {
                        console.error('[AuthCallback] Code exchange error:', error);
                        redirectDoneRef.current = true;
                        logRedirect('/auth/callback', '/login', 'code exchange failed');
                        navigate('/login?error=oauth_failed', { replace: true });
                        return;
                    }
                    
                    if (import.meta.env.DEV) {
                        console.log('[AuthCallback] Code exchanged successfully, session:', !!data.session);
                    }
                    
                    // Wait a moment for session to propagate
                    await new Promise(resolve => setTimeout(resolve, 300));
                } catch (err) {
                    console.error('[AuthCallback] Error during code exchange:', err);
                    redirectDoneRef.current = true;
                    logRedirect('/auth/callback', '/login', 'code exchange exception');
                    navigate('/login?error=oauth_failed', { replace: true });
                    return;
                }
            }
            
            // Get current session (after code exchange or if already exists)
            const { data: { session: currentSession } } = await supabase.auth.getSession();
            
            if (!currentSession || !currentSession.user) {
                redirectDoneRef.current = true;
                logRedirect('/auth/callback', '/login', 'no session after callback');
                navigate('/login?error=callback_failed', { replace: true });
                return;
            }
            
            // Ensure profile exists for OAuth users (only check once)
            if (!profileCheckedRef.current) {
                profileCheckedRef.current = true;
                await ensureProfileForOAuthUser(
                    currentSession.user.id,
                    currentSession.user.email,
                    currentSession.user.user_metadata
                );
            }
            
            // Fetch profile to check completion status
            const { data: profileData, error: profileError } = await supabase
                .from('profiles')
                .select('first_name, last_name, firstName, lastName')
                .eq('id', currentSession.user.id)
                .maybeSingle();
            
            if (import.meta.env.DEV) {
                console.log('[AuthCallback] Profile check:', {
                    hasProfile: !!profileData,
                    profileError: profileError?.code,
                    firstName: profileData?.first_name || profileData?.firstName,
                    lastName: profileData?.last_name || profileData?.lastName
                });
            }
            
            // Determine route based on profile completion
            const isComplete = profileData ? isProfileCompleteAny(profileData) : false;
            const targetRoute = isComplete ? '/marketplace' : '/onboarding';
            
            redirectDoneRef.current = true;
            logRedirect('/auth/callback', targetRoute, isComplete ? 'profile complete' : 'profile incomplete');
            navigate(targetRoute, { replace: true });
        };

        // Small delay to ensure Supabase has processed the callback
        const timer = setTimeout(() => {
            if (redirectDoneRef.current) return;
            handleOAuthCallback();
        }, 500);

        return () => clearTimeout(timer);
    }, [authReady, session, userId, navigate, location.search]);

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-white">
            <Loader2 className="size-10 animate-spin text-slate-900 mb-4" />
            <h2 className="text-xl font-bold text-slate-900">Completing Sign In...</h2>
            <p className="text-slate-500">Please wait while we verify your credentials.</p>
        </div>
    );
};

export default AuthCallback;

/* 
=============================================================================
DASHBOARD CHECKLIST: Google OAuth Setup
=============================================================================

1. SUPABASE DASHBOARD:
   - Go to: Authentication → Providers → Google
   - Enable Google provider
   - Add your Google OAuth credentials (Client ID & Client Secret)

2. GOOGLE CLOUD CONSOLE:
   - Create OAuth 2.0 credentials at: https://console.cloud.google.com/apis/credentials
   - Application type: Web application
   - Authorized redirect URIs (add ALL of these):
     * http://localhost:5173/auth/callback (local dev)
     * https://your-domain.vercel.app/auth/callback (production)
     * https://your-project.supabase.co/auth/v1/callback (Supabase default)

3. SUPABASE REDIRECT URLS:
   - Go to: Authentication → URL Configuration
   - Site URL: https://your-domain.vercel.app
   - Additional Redirect URLs:
     * https://your-domain.vercel.app/*
     * https://your-domain.vercel.app/auth/callback
     * http://localhost:5173/auth/callback (for local dev)

4. VERIFY:
   - Test locally: Click "Continue with Google" → Should redirect to Google → Back to /auth/callback → Then /dashboard or /onboarding
   - Test production: Same flow on Vercel domain
   - Check browser console for any OAuth errors

5. NOTES:
   - Profile is auto-created via database trigger on first OAuth login
   - If Google provides full_name, it's parsed into first_name/last_name
   - Users without first_name/last_name are routed to /onboarding
   - Users with complete profiles go to /dashboard

=============================================================================
*/
